import { readCookie } from '@verv-inc/js-toolkit';
import { IParams } from 'interfaces';
import { OneLinkParameters, ExtraData } from './VervAPITypes';
import { IExtraDataPromocode } from '@types';

export interface IVervAPIAttributionHelper {
	getLanguage: () => string;
	getExtradataPromocode: () => string | null;
	getSourceURL: () => string;
	getFBP: () => string | null;
	getFBClickId: () => string | null;
	getPid: () => string | null;
	getCampaign: () => string;
	prepareOneLinkParameters: () => OneLinkParameters;
	prepareExtraData: () => ExtraData;
}

export const VervAPIAttributionHelper = (
	parameters: IParams,
	extraDataPromocode: IExtraDataPromocode | undefined,
	sourceLocation: Location,
): IVervAPIAttributionHelper => {
	const getLanguage = (): string => {
		return parameters.force_lang ?? window?.navigator?.language.slice(0, 2) ?? '';
	};

	const getSourceURL = () => sourceLocation.href;

	const getExtradataPromocode = (): string | null => {
		return extraDataPromocode?.promocode ?? null;
	};

	const getPid = (): string | null => {
		if (parameters.pid) return parameters.pid;
		if (parameters.af_pid) return parameters.af_pid;
		if (parameters.utm_source) return parameters.utm_source;
		return null;
	};

	const getFBP = (): string | null => {
		if (parameters.fbp) return parameters.fbp;
		const _fbp = readCookie('_fbp');
		if (_fbp) return _fbp;
		return null;
	};

	const getFBClickId = (): string | null => {
		if (parameters.fbc) return parameters.fbc;
		const _fbc = readCookie('_fbc');
		if (_fbc) return _fbc;
		if (parameters.fbclid) return `fb.1.${Date.now()}.${parameters.fbclid}`;
		return null;
	};

	const getCampaign = (): string => {
		if (parameters.c) return parameters.c;
		if (parameters.af_c) return parameters.af_c;
		if (parameters.utm_campaign) return parameters.utm_campaign;

		return 'paid-web-onboarding';
	};

	const getWebPurchaseType = (): string | undefined => {
		if (parameters.academyPlanVersion || parameters.newAcademyPlanVersion) return 'wla';
		return undefined;
	};

	const getCustomProductPageIOS = (): string | undefined => {
		if (parameters.academyPlanVersion || parameters.newAcademyPlanVersion)
			return '7a83a1b7-f9ea-439c-8d7e-275ae9dd0dea';
		return undefined;
	};

	const prepareOneLinkParameters = (): OneLinkParameters => {
		const pid = getPid();
		const campaignValue = getCampaign();
		const customProductPageIOS = getCustomProductPageIOS();

		const AF_PRT = parameters.af_prt;
		const AF_ADSET = parameters.utm_term;
		const AF_AD = parameters.utm_content;
		const UTM_ID = parameters.utm_id;

		return {
			...(pid && { pid }),
			...(campaignValue && { c: campaignValue }),
			...(AF_PRT && { af_prt: AF_PRT }),
			...(AF_ADSET && { af_adset: AF_ADSET }),
			...(AF_AD && { af_ad: AF_AD }),
			...(UTM_ID && { utm_id: UTM_ID }),
			...(customProductPageIOS && { af_ios_store_cpp: customProductPageIOS }),
			deep_link_value: 'paid-web-onboarding',
		};
	};

	const prepareExtraData = (): ExtraData => {
		const gender = parameters.gender;
		const fbp = getFBP();
		const language = getLanguage();
		const pid = getPid() ?? '';
		const campaign = getCampaign();
		const sourceURL = getSourceURL();
		const webPurchaseType = getWebPurchaseType();

		return {
			...(fbp && { fbp }),
			...(gender && { gender }),
			...(webPurchaseType && { web_purchase_type: webPurchaseType }),
			source_key: 'verv-payment-page',
			source_url: sourceURL,
			language,
			pid,
			campaign,
		};
	};

	return {
		getLanguage,
		getExtradataPromocode,
		getSourceURL,
		getFBP,
		getPid,
		getFBClickId,
		getCampaign,
		prepareOneLinkParameters,
		prepareExtraData,
	};
};
